import React, {useState} from 'react';
import {Table, Card, Button, Modal, Space, Input, Select} from 'antd';
import {useTheme} from "@/components/common/ThemeProvider";
import {usePaymentConfigurations, useUpdatePaymentConfiguration} from '../hooks/usePaymentConfig';
import {showSuccessNotification, showErrorNotification} from '@/components/common/utils/notify';
import {PaymentConfigurationDTO} from '@/shared/api/model';
import {SearchOutlined} from "@ant-design/icons";
import {useGetGlAccounts} from "@/shared/api/client/coa-resource";
import {AccountSelectorProps} from "@/modules/payment-config/components/AccountSelector";

interface MappingModalProps {
    configId: string;
    acType: string;
    visible: boolean;
    onClose: () => void;
    onSuccess: () => void;
    dark?: boolean;
}


const PaymentConfigurationView: React.FC = () => {
    const {dark} = useTheme();
    const {data, isLoading, refetch} = usePaymentConfigurations();
    const [selectedConfig, setSelectedConfig] = useState<PaymentConfigurationDTO | null>(null);
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');

    const configurations = data ?? [];

    const getColumnSearchProps = (dataIndex: string) => ({
        filterDropdown: ({setSelectedKeys, selectedKeys, confirm, clearFilters}: any) => (
            <div style={{padding: 8}}>
                <Input
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
                    style={{width: 188, marginBottom: 8, display: 'block'}}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
                        icon={<SearchOutlined/>}
                        size="small"
                        style={{width: 90}}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => handleReset(clearFilters)}
                        size="small"
                        style={{width: 90}}
                    >
                        Reset
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{color: filtered ? '#1890ff' : undefined}}/>
        ),
        onFilter: (value: string, record: any) =>
            record[dataIndex]
                ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase())
                : false,
    });

    const handleSearch = (selectedKeys: string[], confirm: () => void, dataIndex: string) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
    };

    const columns = [
        {
            title: 'Configuration Type',
            dataIndex: ['configurationType', 'name'],
            key: 'configType',
            ...getColumnSearchProps('configurationType.name'),
            sorter: (a: any, b: any) =>
                a.configurationType.name.localeCompare(b.configurationType.name)
        },
        {
            title: 'FSP',
            dataIndex: 'fspCode',
            key: 'fsp',
            ...getColumnSearchProps('fspCode'),
            sorter: (a: any, b: any) => a.fspCode.localeCompare(b.fspCode)
        },
        {
            title: 'Account',
            dataIndex: 'accountName',
            key: 'account',
            ...getColumnSearchProps('accountName'),
            render: (text: string) => text || 'Not mapped',
            sorter: (a: any, b: any) => {
                const aName = a.accountName || '';
                const bName = b.accountName || '';
                return aName.localeCompare(bName);
            }
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (record: PaymentConfigurationDTO) => (
                <Button
                    onClick={() => setSelectedConfig(record)}
                    type="primary"
                >
                    Map Account
                </Button>
            )
        }
    ];

    return (
        <div className={dark ? 'dark-mode' : ''}>
            <Card title="Payment Configurations">
                <Table
                    dataSource={configurations}
                    // @ts-ignore
                    columns={columns}
                    loading={isLoading}
                    rowKey="id"
                    pagination={{pageSize: 10}}
                />
                {selectedConfig && (
                    <MappingModal
                        // @ts-ignore
                        configId={selectedConfig.id}
                        // @ts-ignore
                        acType={selectedConfig.configurationType.acType}
                        visible={!!selectedConfig}
                        onClose={() => setSelectedConfig(null)}
                        onSuccess={refetch}
                        dark={dark}
                    />
                )}
            </Card>
        </div>
    );
};

// Update the AccountSelector component
const AccountSelector: React.FC<AccountSelectorProps> = ({
                                                             acType,
                                                             value,
                                                             onChange,
                                                             dark
                                                         }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const {data: accounts, isLoading} = useGetGlAccounts('AC', {
        q: searchQuery,
        // @ts-ignore
        acType: acType
    });

    return (
        <Select
            showSearch
            value={value}
            onChange={onChange}
            loading={isLoading}
            className={dark ? 'dark-mode-select' : ''}
            filterOption={false} // Disable browser-side filtering
            onSearch={setSearchQuery} // Handle search on the server side
            style={{width: '100%'}} // Make select fill the available space
            options={accounts?.map(account => ({
                label: `${account.glName} (${account.accountId})`,
                value: account.accountId
            }))}
            placeholder="Search and select an account"
        />
    );
};

// Update the MappingModal styles
const MappingModal: React.FC<MappingModalProps> = ({
                                                       configId,
                                                       acType,
                                                       visible,
                                                       onClose,
                                                       onSuccess,
                                                       dark
                                                   }) => {
    const [selectedAccount, setSelectedAccount] = useState<string>();
    const updateConfigMutation = useUpdatePaymentConfiguration();

    const handleSubmit = async () => {
        if (!selectedAccount) return;
        try {
            // Properly structure the mutation data according to the API spec
            await updateConfigMutation.mutateAsync({
                configId: configId,
                params: {
                    accountId: selectedAccount,
                    accountName: "Account name" // You might want to get this from the selected account details
                }
            });
            showSuccessNotification('Success', 'Configuration updated successfully', dark);
            onSuccess();
            onClose();
        } catch (error) {
            showErrorNotification('Error', 'Failed to update configuration', dark);
        }
    };

    return (
        <Modal
            title="Map Account"
            open={visible}
            onCancel={onClose}
            onOk={handleSubmit}
            okButtonProps={{loading: updateConfigMutation.isPending}}
            width={600} // Increase modal width
        >
            <div style={{padding: '20px 0'}}>
                <AccountSelector
                    acType={acType}
                    value={selectedAccount}
                    onChange={setSelectedAccount}
                    dark={dark}
                />
            </div>
        </Modal>
    );
};

export default PaymentConfigurationView;